import React, { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/** Selected play is a bit larger than the chip at rest, so the drag reads as a pickup. */
export const TOUCH_DRAG_GHOST_SCALE = 1.15;

/**
 * Copy the dragged chip's layout size onto the floating preview, then grow it slightly.
 * The preview is portaled to document.body, so parent-scoped rules
 * (clash-sheet badge size, carousel card size) would otherwise be lost
 * and the icon would render at the wrong size.
 * @param {HTMLElement | null | undefined} sourceEl
 * @param {HTMLElement | null | undefined} clone
 * @param {number} [scale]
 */
export function applyTouchDragGhostSize(sourceEl, clone, scale = TOUCH_DRAG_GHOST_SCALE) {
  if (!sourceEl || !clone) return;
  clone.classList.remove('augment-card--dragging');
  const grown = (px) => `${Math.round(px * scale)}px`;
  const width = sourceEl.offsetWidth;
  const height = sourceEl.offsetHeight;
  if (width > 0) {
    const next = grown(width);
    clone.style.width = next;
    clone.style.minWidth = next;
    clone.style.maxWidth = next;
  }
  if (height > 0) {
    const next = grown(height);
    clone.style.height = next;
    clone.style.minHeight = next;
    clone.style.maxHeight = next;
  }
  clone.style.transform = 'none';
  clone.style.opacity = '1';
  clone.style.margin = '0';
  clone.style.flex = 'none';
  clone.style.aspectRatio = 'auto';
  clone.style.pointerEvents = 'none';

  const sourceImgs = sourceEl.querySelectorAll('img');
  const cloneImgs = clone.querySelectorAll('img');
  sourceImgs.forEach((img, i) => {
    const cloneImg = cloneImgs[i];
    if (!cloneImg) return;
    const imgWidth = img.offsetWidth;
    const imgHeight = img.offsetHeight;
    if (imgWidth > 0) {
      const next = grown(imgWidth);
      cloneImg.style.width = next;
      cloneImg.style.maxWidth = next;
    }
    if (imgHeight > 0) {
      const next = grown(imgHeight);
      cloneImg.style.height = next;
      cloneImg.style.maxHeight = next;
    }
  });
}

/**
 * Desktop drag image, same slight growth as the touch preview.
 * @param {DragEvent} event
 * @param {HTMLElement | null | undefined} sourceEl
 */
export function setScaledDragImage(event, sourceEl) {
  if (!event?.dataTransfer || !sourceEl || typeof document === 'undefined') return;
  const clone = sourceEl.cloneNode(true);
  clone.removeAttribute('draggable');
  applyTouchDragGhostSize(sourceEl, clone);
  clone.style.position = 'fixed';
  clone.style.left = '-9999px';
  clone.style.top = '0';
  document.body.appendChild(clone);
  const offsetX = Math.round((sourceEl.offsetWidth * TOUCH_DRAG_GHOST_SCALE) / 2);
  const offsetY = Math.round((sourceEl.offsetHeight * TOUCH_DRAG_GHOST_SCALE) / 2);
  event.dataTransfer.setDragImage(clone, offsetX, offsetY);
  setTimeout(() => clone.remove(), 0);
}

/**
 * Floating copy of the chip under the finger, slightly larger than the chip at rest.
 */
export default function TouchDragGhost({ x, y, sourceEl, zIndex = 9999 }) {
  const hostRef = useRef(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !sourceEl) return undefined;
    const clone = sourceEl.cloneNode(true);
    clone.removeAttribute('draggable');
    applyTouchDragGhostSize(sourceEl, clone);
    host.replaceChildren(clone);
    return () => {
      host.replaceChildren();
    };
  }, [sourceEl]);

  if (x == null || y == null || !sourceEl) return null;

  return ReactDOM.createPortal(
    <div
      ref={hostRef}
      className="touch-drag-ghost"
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex,
        willChange: 'transform'
      }}
    />,
    document.body
  );
}
